import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { localiApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import LogoUpload from '../components/LogoUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  Plus,
  Loader2,
  Pencil,
  Power,
  PowerOff,
  UtensilsCrossed,
  Pizza,
  Wine,
} from 'lucide-react';
import { cn } from '../lib/utils';

const TIPI = [
  { value: 'ristorante', label: 'Ristorante', icon: UtensilsCrossed },
  { value: 'pizzeria', label: 'Pizzeria', icon: Pizza },
  { value: 'bar', label: 'Bar', icon: Wine },
];

const GIORNI = [
  { value: 'LUN', label: 'Lunedì' },
  { value: 'MAR', label: 'Martedì' },
  { value: 'MER', label: 'Mercoledì' },
  { value: 'GIO', label: 'Giovedì' },
  { value: 'VEN', label: 'Venerdì' },
  { value: 'SAB', label: 'Sabato' },
  { value: 'DOM', label: 'Domenica' },
];

const tipoLabel = (value) => TIPI.find((t) => t.value === value)?.label || value;
const tipoIcon = (value) => TIPI.find((t) => t.value === value)?.icon || Building2;

const emptyForm = { nome: '', tipo: 'ristorante', logo: '', giorni_apertura: ['MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'] };

const LocaliPage = () => {
  const navigate = useNavigate();
  const { canManageLocali } = useAuth();
  const [locali, setLocali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!canManageLocali()) {
      navigate('/impostazioni', { replace: true });
      return;
    }
    loadLocali();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocali = async () => {
    try {
      const data = await localiApi.getAll(true);
      setLocali(data);
    } catch (e) {
      toast.error('Errore nel caricamento locali');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowFormDialog(true);
  };

  const openEdit = (locale) => {
    setEditing(locale);
    setForm({
      nome: locale.nome,
      tipo: locale.tipo,
      logo: locale.logo || '',
      giorni_apertura: locale.giorni_apertura || [],
    });
    setShowFormDialog(true);
  };

  const toggleGiorno = (g) => {
    setForm((prev) => ({
      ...prev,
      giorni_apertura: prev.giorni_apertura.includes(g)
        ? prev.giorni_apertura.filter((x) => x !== g)
        : [...prev.giorni_apertura, g],
    }));
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      toast.error('Inserisci il nome del locale');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        tipo: form.tipo,
        logo: form.logo || null,
        giorni_apertura: form.giorni_apertura,
      };
      if (editing) {
        await localiApi.update(editing.id, payload);
        toast.success('Locale aggiornato');
      } else {
        await localiApi.create(payload);
        toast.success('Locale creato');
      }
      setShowFormDialog(false);
      setForm(emptyForm);
      setEditing(null);
      loadLocali();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'operazione");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStato = async (locale) => {
    try {
      if (locale.stato === 'attivo') {
        await localiApi.deactivate(locale.id);
        toast.success(`${locale.nome} disattivato`);
      } else {
        await localiApi.activate(locale.id);
        toast.success(`${locale.nome} riattivato`);
      }
      loadLocali();
    } catch (e) {
      toast.error("Errore nel cambio stato");
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/impostazioni')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-heading tracking-tight">Gestione Locali</h1>
              <p className="text-xs text-muted-foreground">
                {locali.filter((l) => l.stato === 'attivo').length} attivi su {locali.length}
              </p>
            </div>
          </div>
          <Dialog open={showFormDialog} onOpenChange={(o) => { setShowFormDialog(o); if (!o) { setForm(emptyForm); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openCreate} data-testid="create-locale-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuovo Locale
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  {editing ? `Modifica ${editing.nome}` : 'Nuovo locale'}
                </DialogTitle>
                <DialogDescription>
                  Compila i dati del locale. Tutti i record (chiusure, uscite) saranno legati al locale corrispondente.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    data-testid="locale-nome-input"
                    placeholder="Es. Santomato"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select
                    value={form.tipo}
                    onValueChange={(value) => setForm({ ...form, tipo: value })}
                  >
                    <SelectTrigger data-testid="locale-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPI.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex items-center gap-2">
                            <t.icon className="w-4 h-4" />
                            <span>{t.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Logo del locale</Label>
                  <LogoUpload
                    value={form.logo || null}
                    onChange={(val) => setForm({ ...form, logo: val })}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Comparirà nella schermata di selezione locale al login e nell'header.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Giorni di apertura</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {GIORNI.map((g) => {
                      const checked = form.giorni_apertura.includes(g.value);
                      return (
                        <button
                          key={g.value}
                          type="button"
                          onClick={() => toggleGiorno(g.value)}
                          data-testid={`giorno-${g.value}`}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors',
                            checked
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background border-border hover:bg-muted'
                          )}
                        >
                          <span
                            className={cn(
                              'inline-flex w-4 h-4 rounded border items-center justify-center text-[10px] font-bold',
                              checked ? 'bg-primary-foreground text-primary' : 'border-muted-foreground/40'
                            )}
                          >
                            {checked ? '✓' : ''}
                          </span>
                          <span>{g.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowFormDialog(false)}>Annulla</Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-locale-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {editing ? 'Salva modifiche' : 'Crea locale'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : locali.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">Nessun locale ancora creato</p>
              <Button className="mt-4" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Crea il primo locale
              </Button>
            </CardContent>
          </Card>
        ) : (
          locali.map((l) => {
            const TipoIcon = tipoIcon(l.tipo);
            const isActive = l.stato === 'attivo';
            return (
              <Card key={l.id} data-testid={`locale-card-${l.id}`} className={cn(!isActive && 'opacity-60 bg-muted/50')}>
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={cn('w-11 h-11 rounded-full overflow-hidden flex items-center justify-center shrink-0', isActive ? 'bg-primary/10' : 'bg-muted')}>
                      {l.logo ? (
                        <img src={l.logo} alt={l.nome} className="w-11 h-11 object-cover" />
                      ) : (
                        <TipoIcon className={cn('w-5 h-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                      )}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold truncate">{l.nome}</p>
                        <Badge variant="outline" className="text-xs">
                          {tipoLabel(l.tipo)}
                        </Badge>
                        {!isActive && <Badge variant="secondary" className="text-xs">Inattivo</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">/{l.slug}</p>
                      <div className="flex flex-wrap gap-1 pt-1">
                        {GIORNI.map((g) => (
                          <span
                            key={g.value}
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded',
                              (l.giorni_apertura || []).includes(g.value)
                                ? 'bg-primary/15 text-primary font-semibold'
                                : 'bg-muted text-muted-foreground/60'
                            )}
                          >
                            {g.value}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(l)}
                      data-testid={`edit-locale-${l.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Modifica
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{isActive ? 'Attivo' : 'Inattivo'}</span>
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => toggleStato(l)}
                        data-testid={`toggle-locale-${l.id}`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        <p className="text-xs text-muted-foreground text-center pt-2">
          <PowerOff className="w-3 h-3 inline mr-1" />
          La disattivazione rende il locale non operativo ma <strong>non elimina</strong> alcun dato storico.
        </p>
      </main>
    </div>
  );
};

export default LocaliPage;
